import {
	Backdrop, Checkbox,
	CircularProgress, FormControlLabel, FormGroup,
	MenuItem,
	Stack,
	TablePagination,
	TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { ManageRequestsTable } from '../../components/ManageRequestsTable';
import { Search } from '../../components/Search';
import { StudyRequestFilter, StudyRequestStatus } from '../../interfaces';
import { useStudyRequestService, useTranslation } from '../../services';
import { isEqual } from 'lodash';

interface AdminManageRequestsInterface {}

const AdminManageRequests: FC<AdminManageRequestsInterface> = () => {
	const pageDefault = 0;
	const rowsPerPageDefault = 25;
	const orderDefault = 'asc';
	const today = dayjs();

	const { t } = useTranslation();
	const { studyRequests, getStudyRequests, meta } = useStudyRequestService();
	const [loading, setLoading] = useState(true);
	const [filters, setFilters] = useState<any>({
		basicFlowOnly: false,
		page: pageDefault + 1,
		limit: rowsPerPageDefault,
		order: orderDefault,
	});
	const [fromDate, setFromDate] = useState<Dayjs | null>(null);
	const [toDate, setToDate] = useState<Dayjs | null>(null);
	const [page, setPage] = useState(pageDefault);
	const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageDefault);
	const [status, setStatus] = useState<StudyRequestStatus | ''>('');
	const [basicFlowOnly, setBasicFlowOnly] = useState(false);

	const statusOptions: Array<{ value: StudyRequestStatus | ''; label: string }> = [
		{ value: '', label: '--//--' },
		...Object.values(StudyRequestStatus).map((val) => ({ value: val, label: val })),
	];

	const handlerFilters = useCallback(
		(val: StudyRequestFilter) => {
			if (val.phone) {
				val.phone = val.phone.replace(/[^0-9.]/g, '');
				val.phone = !!val.phone ? val.phone : 'undefined';
			}

			if (val.search) {
				const value = val.search && val.search.replace(/[^0-9.]/g, '');
				const isPhoneNumber = value && value.length === 12;
				val.search = isPhoneNumber ? val.search.replace(/[^0-9.]/g, '') : val.search;
			}

			let newFilters = { ...filters, ...val };
			Object.keys(newFilters).forEach(
				(key) => newFilters[key] === undefined && delete newFilters[key]
			);
			if (!isEqual(filters, newFilters)) {
				if (!val.page)  {
					newFilters = {
						...newFilters,
						page: 1
					}
				}
				setFilters({ ...newFilters });
			}
		},
		[filters, setFilters]
	);

	const handleChangeStatus = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value as StudyRequestStatus | '';
			setStatus(value);
			if (value !== '') {
				handlerFilters({ status: value });
			} else {
				handlerFilters({ status: undefined });
			}
		},
		[handlerFilters, setStatus]
	);

	const handleChangeBasicFlowOnly = useCallback(
		() => {
			setBasicFlowOnly(!basicFlowOnly)
		},
		[basicFlowOnly]
	);

	const handleSearch = useCallback(
		({ searchBy, value }: { searchBy: string; value: string }) => {
			if (value === '') {
				handlerFilters({
					unit: undefined,
					email: undefined,
					phone: undefined,
					search: undefined,
					'basicFlowEmail': undefined,
				});
			} else {
				switch (searchBy) {
					case 'responsible person email':
						searchBy = 'email';
						break;
					case 'basic flow email':
						searchBy = 'basicFlowEmail';
						break;
				}
				handlerFilters({
					unit: undefined,
					email: undefined,
					phone: undefined,
					search: undefined,
					basicFlowEmail: undefined,
					[searchBy]: value,
				});
			}
		},
		[handlerFilters]
	);

	const handleFromDateChange = useCallback(
		(newValue: Dayjs | null) => {
			let newISOValue;

			if ((newValue && newValue.isBefore(toDate)) || (newValue?.isValid() && !toDate)) {
				try {
					setFromDate(newValue);
					newISOValue = newValue.toISOString();
					handlerFilters({ submitDateStart: newISOValue });
				} catch (e) {}
			} else {
				if(newValue===null || newValue?.isValid()){
					setFromDate(null);
				}
				handlerFilters({ submitDateStart: undefined });
			}
		},
		[handlerFilters, toDate]
	);

	const handleTODateChange = useCallback(
		(newValue: Dayjs | null) => {
			let newISOValue;
			if ((newValue && newValue.isAfter(fromDate)) || (newValue?.isValid() && !fromDate)) {
				try {
					setToDate(newValue);
					newISOValue = newValue.toISOString();
					handlerFilters({ submitDateEnd: newISOValue });
				} catch (e) {}
			} else {
				if(newValue===null || newValue?.isValid()){
					setToDate(null);
				}
				handlerFilters({ submitDateEnd: undefined });
			}
		},
		[fromDate, handlerFilters]
	);

	const handleChangePage = useCallback(
		(event: unknown, newPage: number) => {
			newPage !== page && setPage(newPage);
		},
		[setPage, page]
	);

	const handleChangeRowsPerPage = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const newRowPerPage = parseInt(event.target.value, 10);
			if (rowsPerPage !== newRowPerPage) {
				setRowsPerPage(newRowPerPage);
				setPage(0);
			}
		},
		[rowsPerPage, setPage, setRowsPerPage]
	);

	useEffect(()=>{
		handlerFilters({ basicFlowOnly });
	}, [basicFlowOnly]);

	useEffect(() => {
		(async () => {
			!loading && setLoading(true);
			try {
				await getStudyRequests(filters);
				setLoading(false);
			}catch (e){
				setLoading(false);
			}

		})();
	}, [filters]);

	useEffect(() => {
		handlerFilters({ page: page + 1, limit: rowsPerPage });
	}, [page, rowsPerPage]);

	return (
		<>
			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={loading}
			>
				<CircularProgress color="inherit" sx={{ m: 1 }} />
				<Stack direction="row" justifyContent="center">
					{t('Please wait...')}
				</Stack>
			</Backdrop>

			<Stack spacing={2} margin={2}>
				<Search
					onSearch={handleSearch}
					fields={['search', 'unit', 'phone', 'responsible person email', 'basic flow email']}
				/>
				<Stack direction="row" spacing={2}>
					<TextField
						select
						label={t('Status')}
						value={status}
						onChange={handleChangeStatus}
						helperText={t('Please select status')}
					>
						{statusOptions.map((option, i) => (
							<MenuItem key={`status_${i}`} value={option.value}>
								{t(option.label)}
							</MenuItem>
						))}
					</TextField>

					<DatePicker
						label={t('Date from')}
						format={t('MM/DD/YYYY')}
						maxDate={toDate||today}
						value={fromDate}
						onChange={handleFromDateChange}
					/>

					<DatePicker
						label={t('Date to')}
						format={t('MM/DD/YYYY')}
						minDate={fromDate||undefined}
						maxDate={today}
						value={toDate}
						onChange={handleTODateChange}
					/>

					<FormGroup>
						<FormControlLabel control={<Checkbox onChange={handleChangeBasicFlowOnly}/>} label={t('Basic flow only')} />
					</FormGroup>

				</Stack>
			</Stack>

			<ManageRequestsTable
				data={studyRequests}
				onFilterChange={handlerFilters}
				filter={filters}
				hidden={loading}
			/>
			<TablePagination
				rowsPerPageOptions={[5, 10, 25]}
				component="div"
				labelRowsPerPage={t('Rows per page')}
				count={meta.totalItems || studyRequests.length}
				rowsPerPage={rowsPerPage}
				page={page}
				showFirstButton={true}
				showLastButton={true}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</>
	);
};

export default AdminManageRequests;
